import React from "react";
import { cn } from "@/lib/utils";
import {
  getNotificationDisplayBody,
  getNotificationNavigationTarget,
  getNotificationPresentation,
} from "@/lib/notifications";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import type { NotificationEntity } from "@/types/notifications";

function formatTimeAgo(
  iso: string,
  locales?: {
    secAgo?: string;
    minAgo?: string;
    hrAgo?: string;
    dayAgo?: string;
  },
) {
  const d = new Date(iso).getTime();
  const diff = Date.now() - d;

  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}${locales?.secAgo ?? "s ago"}`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}${locales?.minAgo ?? "m ago"}`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}${locales?.hrAgo ?? "h ago"}`;
  const day = Math.floor(hr / 24);
  return `${day}${locales?.dayAgo ?? "d ago"}`;
}

export function NotificationRow(props: {
  n: NotificationEntity;
  onMarkRead?: (id: string) => void;
}) {
  const { n, onMarkRead } = props;

  const navigate = useNavigate();
  const { userProfile } = useAuthenticatedUser();

  const { t } = useTranslation();
  const presentation = getNotificationPresentation(n);
  const displayBody = getNotificationDisplayBody(n, t);
  const navigationTarget = getNotificationNavigationTarget(n, userProfile);
  const Icon = presentation.icon;

  const handleNotificationsClick = () => {
    if (!navigationTarget) return;
    navigate(navigationTarget);
  };

  return (
    <div
      className={cn(
        "relative flex items-start gap-4",
        "rounded-[8px] border bg-white",
        "px-5 py-4",
        "shadow-xs transition-shadow",
        navigationTarget ? "cursor-pointer hover:shadow-md" : "",
      )}
      onClick={handleNotificationsClick}
    >
      {/* left icon */}
      <div className="mt-0.5 shrink-0">
        <Icon className={cn("h-4 w-4", presentation.iconClassName)} />
      </div>

      {/* content */}
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            "text-[14px] leading-[20px] font-semibold",
            presentation.titleClassName,
          )}
        >
          {n.title}
        </div>

        <div className="mt-[2px] text-[14px] leading-[20px] text-muted-foreground">
          {displayBody}
        </div>

        <div className="mt-2 text-[12px] leading-[16px] text-muted-foreground">
          {formatTimeAgo(n.createdAt, {
            secAgo: t("notifications.secAgo"),
            minAgo: t("notifications.minAgo"),
            hrAgo: t("notifications.hrAgo"),
            dayAgo: t("notifications.dayAgo"),
          })}
        </div>
      </div>

      {/* right action */}
      {!n.isRead ? (
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-full shrink-0"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            onMarkRead?.(n.id);
          }}
          aria-label={t("notifications.markAsRead")}
          title={t("notifications.markAsRead")}
        >
          <Check className="h-5 w-5 text-muted-foreground" />
        </Button>
      ) : null}

      {/* unread dot */}
      {!n.isRead ? (
        <span
          className={cn(
            "absolute right-3 top-3 h-2.5 w-2.5 rounded-full",
            presentation.unreadDotClassName,
          )}
          aria-label={t("notifications.unread")}
        />
      ) : null}
    </div>
  );
}
